import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckSquare, Square, Plus, X, Trash2, Edit2, Search, Eye, EyeOff, AlertCircle, AlertTriangle, ArrowDown, Clock, Play, MessageSquare, Send, ChevronDown, ChevronUp, Flame, Lightbulb } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityConfig = {
  1: { label: 'High', color: 'text-red-400', bgColor: 'bg-red-900/30', borderColor: 'border-red-700', icon: AlertCircle },
  2: { label: 'Medium', color: 'text-amber-400', bgColor: 'bg-amber-900/30', borderColor: 'border-amber-700', icon: AlertTriangle },
  3: { label: 'Low', color: 'text-blue-400', bgColor: 'bg-blue-900/30', borderColor: 'border-blue-700', icon: ArrowDown },
  4: { label: 'Idea', color: 'text-purple-400', bgColor: 'bg-purple-900/30', borderColor: 'border-purple-700', icon: Lightbulb }
};

const statusConfig = {
  critical: { label: 'CRITICAL', color: 'text-white', bgColor: 'bg-red-600', icon: Flame },
  pending: { label: 'Pending', color: 'text-slate-400', bgColor: 'bg-slate-700', icon: Clock },
  started: { label: 'Started', color: 'text-amber-400', bgColor: 'bg-amber-900/40', icon: Play },
  needs_testing: { label: 'Needs Testing', color: 'text-cyan-400', bgColor: 'bg-cyan-900/40', icon: AlertTriangle },
  completed: { label: 'Completed', color: 'text-teal-400', bgColor: 'bg-teal-900/40', icon: CheckSquare }
};

// ============================================
// STANDALONE NOTE INPUT - Completely isolated
// ============================================
const StandaloneNoteInput = ({ todoId, onNoteAdded }) => {
  const inputRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hasText, setHasText] = useState(false);

  const handleSubmit = async () => {
    const value = inputRef.current?.value?.trim();
    if (!value) return;
    
    setIsAdding(true);
    try {
      await axios.post(`${API}/admin/todos/${todoId}/notes`, { text: value });
      if (inputRef.current) {
        inputRef.current.value = '';
        setHasText(false);
      }
      if (onNoteAdded) onNoteAdded();
      toast.success('Note added');
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={inputRef}
        placeholder="Add a note..."
        defaultValue=""
        onInput={(e) => setHasText(e.target.value.trim().length > 0)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        className="flex-1 min-h-[36px] resize-none overflow-hidden bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        data-testid={`note-input-${todoId}`}
      />
      <Button
        onClick={handleSubmit}
        disabled={isAdding || !hasText}
        size="sm"
        className="bg-teal-500 hover:bg-teal-400 text-slate-900 h-9 px-3"
      >
        {isAdding ? <span className="animate-spin">⏳</span> : <Send className="h-3 w-3" />}
      </Button>
    </div>
  );
};

// ============================================
// STANDALONE TODO ITEM - Defined OUTSIDE TodoList
// ============================================
const TodoItemComponent = memo(({ 
  todo, 
  isCompleted,
  isExpanded,
  isEditing,
  editTitle,
  editDescription, 
  editStatus,
  editPriority,
  onToggleExpand,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSetEditTitle,
  onSetEditDescription,
  onSetEditStatus,
  onSetEditPriority,
  onUpdateStatus,
  onDelete,
  onDeleteNote,
  onNoteAdded
}) => {
  const config = priorityConfig[todo.priority] || priorityConfig[2];
  const notes = todo.notes || [];

  // Edit mode
  if (isEditing) {
    return (
      <div className="p-4 rounded-lg border bg-slate-700 border-slate-500">
        <Input
          placeholder="Task title"
          value={editTitle}
          onChange={(e) => onSetEditTitle(e.target.value)}
          className="mb-2 bg-slate-800 border-slate-600 text-slate-100"
        />
        <textarea
          placeholder="Description (optional)"
          value={editDescription}
          onChange={(e) => onSetEditDescription(e.target.value)}
          className="w-full min-h-[60px] resize-none overflow-hidden bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-2"
        />
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Status:</span>
            <Select value={editStatus} onValueChange={onSetEditStatus}>
              <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-600 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="critical" className="text-red-500 font-bold">CRITICAL</SelectItem>
                <SelectItem value="pending" className="text-slate-300">Pending</SelectItem>
                <SelectItem value="started" className="text-amber-400">Started</SelectItem>
                <SelectItem value="needs_testing" className="text-cyan-400">Needs Testing</SelectItem>
                <SelectItem value="completed" className="text-teal-400">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Priority:</span>
            <Select value={editPriority} onValueChange={onSetEditPriority}>
              <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-600 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="1" className="text-red-400">High</SelectItem>
                <SelectItem value="2" className="text-amber-400">Medium</SelectItem>
                <SelectItem value="3" className="text-blue-400">Low</SelectItem>
                <SelectItem value="4" className="text-purple-400">Idea</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={onSaveEdit} className="bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-full" size="sm">
            Save
          </Button>
          <Button onClick={onCancelEdit} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-600 rounded-full" size="sm">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Normal display mode
  return (
    <div
      className={`rounded-lg border transition-colors overflow-hidden ${
        isCompleted 
          ? 'bg-teal-900/20 border-teal-800' 
          : todo.status === 'critical'
            ? 'bg-red-900/50 border-red-500 border-2 shadow-lg shadow-red-900/30 animate-pulse'
            : todo.status === 'started'
              ? 'bg-amber-900/20 border-amber-500 border-2'
              : todo.status === 'needs_testing'
                ? 'bg-cyan-900/20 border-cyan-500 border-2'
                : `${config.bgColor} ${config.borderColor}`
      }`}
    >
      {/* Status bar at top */}
      {todo.status === 'started' && !isCompleted && (
        <div className="bg-amber-500 text-slate-900 text-xs font-bold text-center py-1 uppercase tracking-wider">
          <Play className="h-3 w-3 inline mr-1" />
          STARTED - IN PROGRESS
        </div>
      )}
      {todo.status === 'needs_testing' && !isCompleted && (
        <div className="bg-cyan-500 text-slate-900 text-xs font-bold text-center py-1 uppercase tracking-wider">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          NEEDS TESTING
        </div>
      )}
      {todo.status === 'critical' && !isCompleted && (
        <div className="bg-red-600 text-white text-xs font-bold text-center py-1 uppercase tracking-wider animate-pulse">
          <Flame className="h-3 w-3 inline mr-1 animate-bounce" />
          CRITICAL - URGENT ACTION REQUIRED
        </div>
      )}

      <div className="flex items-start gap-3 p-3">
        {/* Status toggle */}
        <Select value={todo.status || 'pending'} onValueChange={(value) => onUpdateStatus(todo.id, value)}>
          <SelectTrigger className="w-8 h-8 p-0 border-0 bg-transparent hover:bg-slate-700/50 rounded">
            {todo.status === 'completed' ? (
              <CheckSquare className="h-5 w-5 text-teal-400 mx-auto" />
            ) : todo.status === 'started' ? (
              <Play className="h-5 w-5 text-amber-400 mx-auto" />
            ) : todo.status === 'needs_testing' ? (
              <AlertTriangle className="h-5 w-5 text-cyan-400 mx-auto" />
            ) : todo.status === 'critical' ? (
              <Flame className="h-5 w-5 text-red-500 mx-auto animate-pulse" />
            ) : (
              <Square className="h-5 w-5 text-slate-500 mx-auto" />
            )}
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-600">
            <SelectItem value="critical" className="text-red-500 font-bold">
              <div className="flex items-center gap-2"><Flame className="h-4 w-4" />CRITICAL</div>
            </SelectItem>
            <SelectItem value="pending" className="text-slate-300">
              <div className="flex items-center gap-2"><Square className="h-4 w-4" />Pending</div>
            </SelectItem>
            <SelectItem value="started" className="text-amber-400">
              <div className="flex items-center gap-2"><Play className="h-4 w-4" />Started</div>
            </SelectItem>
            <SelectItem value="needs_testing" className="text-cyan-400">
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Needs Testing</div>
            </SelectItem>
            <SelectItem value="completed" className="text-teal-400">
              <div className="flex items-center gap-2"><CheckSquare className="h-4 w-4" />Completed</div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`font-medium ${isCompleted ? 'text-slate-500 line-through' : 'text-slate-100'}`}>
            {todo.title}
          </p>
          {todo.description && (
            <p className={`text-sm mt-1 ${isCompleted ? 'text-slate-600' : 'text-slate-400'}`}>
              {todo.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {/* Priority badge */}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color} ${config.bgColor}`}>
              {config.label}
            </span>
            {/* Notes toggle */}
            <button
              onClick={() => onToggleExpand(todo.id)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
            >
              <MessageSquare className="h-3 w-3" />
              {notes.length} notes
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button onClick={() => onStartEdit(todo)} className="p-1 text-slate-500 hover:text-blue-400 transition-colors">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(todo.id)} className="p-1 text-slate-500 hover:text-red-400 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Notes section */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-700/50 ml-11">
          {notes.length > 0 && (
            <div className="space-y-2 mb-3">
              {notes.map((note) => (
                <div key={note.id} className="bg-slate-800/50 rounded p-2 group">
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-sm text-slate-300 whitespace-pre-wrap flex-1">{note.text}</p>
                    <button
                      onClick={() => onDeleteNote(todo.id, note.id)}
                      className="p-1 text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              ))}
            </div>
          )}
          <StandaloneNoteInput todoId={todo.id} onNoteAdded={onNoteAdded} />
        </div>
      )}
    </div>
  );
});

// Auto-expanding textarea component - memoized to prevent unnecessary re-renders
const AutoExpandTextarea = memo(({ value, onChange, placeholder, className, ...props }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full min-h-[60px] resize-none overflow-hidden bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${className}`}
      {...props}
    />
  );
});

// Separate isolated component for Add Task form to prevent re-renders
const AddTaskForm = memo(({ onAddTask, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('pending');
  const [priority, setPriority] = useState('2');
  const descRef = useRef(null);

  // Auto-expand description textarea
  useEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = 'auto';
      descRef.current.style.height = descRef.current.scrollHeight + 'px';
    }
  }, [description]);

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    onAddTask({
      title: title.trim(),
      description: description.trim(),
      status,
      priority: parseInt(priority)
    });
    // Reset form
    setTitle('');
    setDescription('');
    setStatus('pending');
    setPriority('2');
  };

  return (
    <div className="mb-4 p-4 bg-slate-700 rounded-lg border border-slate-600" data-testid="add-task-form">
      <input
        type="text"
        placeholder="Task title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full mb-2 bg-slate-800 border border-slate-600 text-slate-100 placeholder:text-slate-500 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        data-testid="task-title-input"
      />
      <textarea
        ref={descRef}
        placeholder="Description (optional) - no character limit"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full mb-2 min-h-[60px] resize-none overflow-hidden bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
        data-testid="task-description-input"
      />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Status:</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-600 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              <SelectItem value="critical" className="text-red-500 font-bold">CRITICAL</SelectItem>
              <SelectItem value="pending" className="text-slate-300">Pending</SelectItem>
              <SelectItem value="started" className="text-amber-400">Started</SelectItem>
              <SelectItem value="needs_testing" className="text-cyan-400">Needs Testing</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Priority:</span>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-600 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              <SelectItem value="1" className="text-red-400">High</SelectItem>
              <SelectItem value="2" className="text-amber-400">Medium</SelectItem>
              <SelectItem value="3" className="text-blue-400">Low</SelectItem>
              <SelectItem value="4" className="text-purple-400">Idea</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button
        onClick={handleSubmit}
        className="bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-full"
        size="sm"
        data-testid="save-task-button"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Task
      </Button>
    </div>
  );
});

export const TodoList = () => {
  const [todos, setTodos] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTodo, setEditingTodo] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('2');
  const [editStatus, setEditStatus] = useState('pending');
  
  // Notes state
  const [expandedNotes, setExpandedNotes] = useState({});
  const [addingNote, setAddingNote] = useState({});

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const response = await axios.get(`${API}/admin/todos`);
      setTodos(response.data);
    } catch (error) {
      setTodos([]);
    } finally {
      setLoading(false);
    }
  };

  const addTodo = useCallback(async (taskData) => {
    try {
      const response = await axios.post(`${API}/admin/todos`, taskData);
      setTodos(prevTodos => [...prevTodos, response.data]);
      setShowAddForm(false);
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/todos/${id}`, {
        status: newStatus
      });
      setTodos(todos.map(t => 
        t.id === id ? { ...t, status: newStatus, completed: newStatus === 'completed' } : t
      ));
      toast.success(`Task marked as ${statusConfig[newStatus].label.toLowerCase()}`);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const updateTodo = async (id) => {
    if (!editTitle.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      await axios.patch(`${API}/admin/todos/${id}`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        priority: parseInt(editPriority),
        status: editStatus
      });
      setTodos(todos.map(t => 
        t.id === id ? { 
          ...t, 
          title: editTitle.trim(), 
          description: editDescription.trim(),
          priority: parseInt(editPriority),
          status: editStatus,
          completed: editStatus === 'completed'
        } : t
      ));
      setEditingTodo(null);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTodo = async (id) => {
    try {
      await axios.delete(`${API}/admin/todos/${id}`);
      setTodos(todos.filter(t => t.id !== id));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleCancelAddForm = useCallback(() => {
    setShowAddForm(false);
  }, []);

  const handleToggleAddForm = useCallback(() => {
    setShowAddForm(prev => !prev);
  }, []);

  const addNote = useCallback(async (todoId, noteText) => {
    if (!noteText?.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setAddingNote(prev => ({ ...prev, [todoId]: true }));
    try {
      const response = await axios.post(`${API}/admin/todos/${todoId}/notes`, {
        text: noteText.trim()
      });
      setTodos(prevTodos => prevTodos.map(t => t.id === todoId ? response.data : t));
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(prev => ({ ...prev, [todoId]: false }));
    }
  }, []);

  const deleteNote = async (todoId, noteId) => {
    try {
      const response = await axios.delete(`${API}/admin/todos/${todoId}/notes/${noteId}`);
      setTodos(todos.map(t => t.id === todoId ? response.data : t));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const toggleNotesExpanded = (todoId) => {
    setExpandedNotes({ ...expandedNotes, [todoId]: !expandedNotes[todoId] });
  };

  const startEditing = (todo) => {
    setEditingTodo(todo.id);
    setEditTitle(todo.title);
    setEditDescription(todo.description || '');
    setEditPriority(String(todo.priority || 2));
    setEditStatus(todo.status || 'pending');
  };

  const cancelEditing = () => {
    setEditingTodo(null);
    setEditTitle('');
    setEditDescription('');
    setEditPriority('2');
    setEditStatus('pending');
  };

  // Sort todos: started first (with yellow bar), then by status (critical, pending), then by priority
  // Priority order: 1=high, 2=medium, 3=low, 4=idea (ideas always at bottom)
  const sortedTodos = [...todos].sort((a, b) => {
    // Started tasks always come first
    if (a.status === 'started' && b.status !== 'started') return -1;
    if (b.status === 'started' && a.status !== 'started') return 1;
    
    // Then critical tasks
    if (a.status === 'critical' && b.status !== 'critical') return -1;
    if (b.status === 'critical' && a.status !== 'critical') return 1;
    
    // Then sort by priority (1=high, 2=medium, 3=low, 4=idea)
    const priorityA = a.priority || 2;
    const priorityB = b.priority || 2;
    return priorityA - priorityB;
  });

  // Filter todos based on completed status and search
  const activeTodos = sortedTodos.filter(todo => todo.status !== 'completed');
  const completedTodos = sortedTodos.filter(todo => todo.status === 'completed');
  
  // Apply search filter to completed todos if showing them
  const filteredCompletedTodos = completedTodos.filter(todo => 
    searchQuery.trim() === '' || 
    todo.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (todo.description && todo.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const PriorityBadge = ({ priority }) => {
    const config = priorityConfig[priority] || priorityConfig[2];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color} ${config.bgColor}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    const isCritical = status === 'critical';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color} ${config.bgColor} ${isCritical ? 'animate-pulse font-bold uppercase' : ''}`}>
        <Icon className={`h-3 w-3 ${isCritical ? 'animate-bounce' : ''}`} />
        {config.label}
      </span>
    );
  };

  return (
    <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-6" data-testid="todo-list-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold font-heading text-slate-100">
          Admin Tasks
        </h3>
        <Button
          onClick={handleToggleAddForm}
          variant="outline"
          size="sm"
          className="rounded-full border-slate-600 text-slate-300 hover:bg-slate-700"
          data-testid="add-task-button"
        >
          {showAddForm ? (
            <>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add Task
            </>
          )}
        </Button>
      </div>

      {showAddForm && (
        <AddTaskForm 
          onAddTask={addTodo} 
          onCancel={handleCancelAddForm} 
        />
      )}

      {/* Active Tasks */}
      <div className="space-y-3 mb-4">
        {loading ? (
          <div className="text-center py-4 text-slate-500">Loading tasks...</div>
        ) : activeTodos.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <p>No active tasks</p>
            <p className="text-sm mt-1">Click "Add Task" to create one</p>
          </div>
        ) : (
          activeTodos.map((todo) => (
            <TodoItemComponent 
              key={todo.id} 
              todo={todo} 
              isCompleted={false}
              isExpanded={expandedNotes[todo.id]}
              isEditing={editingTodo === todo.id}
              editTitle={editTitle}
              editDescription={editDescription}
              editStatus={editStatus}
              editPriority={editPriority}
              onToggleExpand={toggleNotesExpanded}
              onStartEdit={(t) => {
                setEditingTodo(t.id);
                setEditTitle(t.title);
                setEditDescription(t.description || '');
                setEditStatus(t.status || 'pending');
                setEditPriority(String(t.priority || 2));
              }}
              onSaveEdit={() => updateTodo(todo.id)}
              onCancelEdit={() => setEditingTodo(null)}
              onSetEditTitle={setEditTitle}
              onSetEditDescription={setEditDescription}
              onSetEditStatus={setEditStatus}
              onSetEditPriority={setEditPriority}
              onUpdateStatus={updateStatus}
              onDelete={deleteTodo}
              onDeleteNote={deleteNote}
              onNoteAdded={fetchTodos}
            />
          ))
        )}
      </div>

      {/* Completed Tasks Section */}
      {completedTodos.length > 0 && (
        <div className="border-t border-slate-700 pt-4">
          <div className="flex items-center justify-between mb-3">
            <Button
              onClick={() => setShowCompleted(!showCompleted)}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-full"
              data-testid="toggle-completed-tasks"
            >
              {showCompleted ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide Completed ({completedTodos.length})
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Show Completed ({completedTodos.length})
                </>
              )}
            </Button>
          </div>

          {showCompleted && (
            <>
              {/* Search for completed tasks */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Search completed tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-500"
                  data-testid="search-completed-tasks"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {filteredCompletedTodos.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    {searchQuery ? 'No matching completed tasks' : 'No completed tasks'}
                  </div>
                ) : (
                  filteredCompletedTodos.map((todo) => (
                    <TodoItemComponent 
                      key={todo.id} 
                      todo={todo} 
                      isCompleted={true}
                      isExpanded={expandedNotes[todo.id]}
                      isEditing={editingTodo === todo.id}
                      editTitle={editTitle}
                      editDescription={editDescription}
                      editStatus={editStatus}
                      editPriority={editPriority}
                      onToggleExpand={toggleNotesExpanded}
                      onStartEdit={(t) => {
                        setEditingTodo(t.id);
                        setEditTitle(t.title);
                        setEditDescription(t.description || '');
                        setEditStatus(t.status || 'pending');
                        setEditPriority(String(t.priority || 2));
                      }}
                      onSaveEdit={() => updateTodo(todo.id)}
                      onCancelEdit={() => setEditingTodo(null)}
                      onSetEditTitle={setEditTitle}
                      onSetEditDescription={setEditDescription}
                      onSetEditStatus={setEditStatus}
                      onSetEditPriority={setEditPriority}
                      onUpdateStatus={updateStatus}
                      onDelete={deleteTodo}
                      onDeleteNote={deleteNote}
                      onNoteAdded={fetchTodos}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
